import { AlertCircle, CreditCard, ShoppingCart, Pause, Play, XCircle } from "lucide-react";
import Button from "../../components/Button";

/**
 * Centralized subscription status banner component
 * Handles all subscription attention states: PAST_DUE, INCOMPLETE, INCOMPLETE_EXPIRED, PAUSED, etc.
 */
export function SubscriptionStatusBanner({
    subscription,
    onRetryPayment,
    onResumeSubscription,
    onUpdatePaymentMethod,
    onPurchaseCredits,
    isLoading,
    retryError,
    retryErrorDetails,
    onDismissError,
    t,
}) {
    if (!subscription) return null;

    const status = subscription.status;
    const requiresAttention = [
        "PAST_DUE",
        "INCOMPLETE",
        "INCOMPLETE_EXPIRED",
        "UNPAID",
    ].includes(status);

    const isPaused = status === "PAUSED";
    const isCancelScheduled = subscription.cancelAtPeriodEnd && status === "ACTIVE";

    if (!requiresAttention && !isPaused && !isCancelScheduled) return null;

    // Determine banner configuration based on status
    const getBannerConfig = () => {
        switch (status) {
            case "INCOMPLETE":
                return {
                    icon: AlertCircle,
                    bgColor: "bg-yellow-50",
                    borderColor: "border-yellow-200",
                    iconBgColor: "bg-yellow-500",
                    title: t("billing.statusBanner.incomplete.title") || "Subscription Incomplete",
                    message: t("billing.statusBanner.incomplete.message") || "Your subscription payment needs to be completed.",
                    actions: [
                        {
                            label: t("billing.statusCards.retryPayment") || "Retry Payment",
                            onClick: onRetryPayment,
                            variant: "primary",
                            icon: "refresh",
                        },
                        {
                            label: t("billing.statusCards.updatePaymentMethod") || "Update Payment Method",
                            onClick: onUpdatePaymentMethod,
                            variant: "secondary",
                            icon: "card",
                        },
                    ],
                };

            case "INCOMPLETE_EXPIRED":
                return {
                    icon: XCircle,
                    bgColor: "bg-red-50",
                    borderColor: "border-red-200",
                    iconBgColor: "bg-red-500",
                    title: t("billing.statusBanner.incompleteExpired.title") || "Subscription Expired",
                    message: t("billing.statusBanner.incompleteExpired.message") || "Your subscription attempt expired. Please subscribe again.",
                    actions: [
                        {
                            label: t("billing.statusBanner.incompleteExpired.action") || "Subscribe Again",
                            onClick: () => window.location.href = "/billing?tab=plans",
                            variant: "primary",
                            icon: "refresh",
                        },
                    ],
                };

            case "PAST_DUE":
                return {
                    icon: AlertCircle,
                    bgColor: "bg-yellow-50",
                    borderColor: "border-yellow-200",
                    iconBgColor: "bg-yellow-500",
                    title: t("billing.statusCards.noticeTitle") || "Payment Required",
                    message: t("billing.statusCards.noticeMessage") || "Your payment failed. Please update your payment method to continue service.",
                    actions: [
                        {
                            label: t("billing.statusCards.retryPayment") || "Retry Payment",
                            onClick: onRetryPayment,
                            variant: "primary",
                            icon: "refresh",
                        },
                        {
                            label: t("billing.statusCards.updatePaymentMethod") || "Update Payment Method",
                            onClick: onUpdatePaymentMethod,
                            variant: "secondary",
                            icon: "card",
                        },
                    ],
                };

            case "UNPAID":
                return {
                    icon: XCircle,
                    bgColor: "bg-red-50",
                    borderColor: "border-red-200",
                    iconBgColor: "bg-red-500",
                    title: t("billing.statusBanner.unpaid.title") || "Payment Overdue",
                    message: t("billing.statusBanner.unpaid.message") || "Your payment is overdue. Service may be suspended.",
                    actions: [
                        {
                            label: t("billing.statusCards.updatePaymentMethod") || "Update Payment Method",
                            onClick: onUpdatePaymentMethod,
                            variant: "primary",
                            icon: "card",
                        },
                    ],
                };

            case "PAUSED":
                return {
                    icon: Pause,
                    bgColor: "bg-blue-50",
                    borderColor: "border-blue-200",
                    iconBgColor: "bg-blue-500",
                    title: t("billing.statusBanner.paused.title") || "Subscription Paused",
                    message: t("billing.statusBanner.paused.message") || "Your subscription is paused. Resume anytime to continue.",
                    actions: [
                        {
                            label: t("billing.statusBanner.paused.action") || "Resume Subscription",
                            onClick: onResumeSubscription,
                            variant: "primary",
                            icon: "play",
                        },
                    ],
                };

            default:
                if (isCancelScheduled) {
                    return {
                        icon: AlertCircle,
                        bgColor: "bg-gray-50",
                        borderColor: "border-gray-200",
                        iconBgColor: "bg-gray-500",
                        title: t("billing.statusBanner.cancelScheduled.title") || "Subscription Ending",
                        message: t("billing.statusBanner.cancelScheduled.message", {
                            date: new Date(subscription.currentPeriodEnd).toLocaleDateString()
                        }) || `Your subscription will end on ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}.`,
                        actions: [
                            {
                                label: t("billing.statusBanner.cancelScheduled.action") || "Reactivate Subscription",
                                onClick: onResumeSubscription,
                                variant: "primary",
                                icon: "play",
                            },
                        ],
                    };
                }
                return null;
        }
    };

    const config = getBannerConfig();
    if (!config) return null;

    const Icon = config.icon;

    const getIconComponent = (iconName) => {
        switch (iconName) {
            case "refresh":
                return (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                );
            case "card":
                return <CreditCard className="w-4 h-4" />;
            case "cart":
                return <ShoppingCart className="w-4 h-4" />;
            case "play":
                return <Play className="w-4 h-4" />;
            default:
                return null;
        }
    };

    return (
        <div className={`p-6 ${config.bgColor} border-2 ${config.borderColor} shadow-lg rounded-lg`}>
            <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                    <div className={`w-10 h-10 rounded-full ${config.iconBgColor} flex items-center justify-center`}>
                        <Icon className="w-6 h-6 text-white" />
                    </div>
                </div>
                <div className="flex-1">
                    <h3 className="text-base font-bold text-gray-900 mb-2">
                        {config.title}
                    </h3>
                    <p className="text-sm text-gray-700 mb-4">
                        {config.message}
                    </p>

                    {/* Error Display */}
                    {retryError && retryErrorDetails && (
                        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg mb-4">
                            <div className="flex items-start">
                                <div className="flex-shrink-0">
                                    <svg className="w-5 h-5 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div className="ml-3 flex-1">
                                    <h4 className="text-sm font-bold text-red-800">
                                        {retryErrorDetails.title}
                                    </h4>
                                    <p className="text-sm text-red-700 mt-1.5 leading-relaxed">
                                        {retryErrorDetails.message}
                                    </p>
                                    {retryErrorDetails.action && (
                                        <p className="text-xs text-red-600 mt-2 font-medium">
                                            💡 {retryErrorDetails.action}
                                        </p>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={onDismissError}
                                    className="ml-3 flex-shrink-0 text-red-400 hover:text-red-600"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-3">
                        {config.actions.map((action, index) => (
                            <Button
                                key={index}
                                onClick={action.onClick}
                                disabled={isLoading}
                                className={`
                                    ${action.variant === "primary"
                                        ? "bg-green-600 hover:bg-green-700 text-white"
                                        : "bg-white hover:bg-gray-50 text-gray-700 border-2 border-gray-300"
                                    }
                                    px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center gap-2
                                `}
                            >
                                {action.icon && getIconComponent(action.icon)}
                                {isLoading ? (t("billing.processing") || "Processing...") : action.label}
                            </Button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
